import {CohortError} from '@cohort/shared-frontend/common/errors';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {CampaignTos} from '@cohort/wallet/components/store/checkout/CampaignTos';
import {Checkbox} from '@cohort/wallet/components/store/checkout/Checkbox';
import useOrderSessionErrors from '@cohort/wallet/hooks/orderSessionErrors';
import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {OrdersApi} from '@cohort/wallet/lib/Endpoints';
import sendMessageToParent from '@cohort/wallet/lib/WindowMessaging';
import type {CampaignStoreWDto} from '@cohort/wallet-schemas/campaign';
import type {OrderWDto} from '@cohort/wallet-schemas/order';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {shallow} from 'zustand/shallow';

type FreeClaimFormProps = {
  campaign: CampaignStoreWDto;
  redeemCode?: string;
  successCallback: (order: OrderWDto) => void;
};

export const FreeClaimForm: React.FC<FreeClaimFormProps> = ({
  campaign,
  redeemCode,
  successCallback,
}) => {
  const [tosChecked, setTosChecked] = useState(false);
  const {t} = useTranslation('components', {keyPrefix: 'store.checkout.freeClaimForm'});
  const [isProcessing, setIsProcessing] = useState(false);
  const {orderTokenId, clearOrderTokenId} = useAppStore(
    store => ({
      orderTokenId: store.orderTokenId,
      clearOrderTokenId: store.clearOrderTokenId,
    }),
    shallow
  );
  const handleOrderSessionErrors = useOrderSessionErrors();

  const createFreeOrder = useApi(OrdersApi.createFreeOrder);

  const handleFreeClaimClick = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setIsProcessing(true);

      try {
        const order = await createFreeOrder(campaign.id, redeemCode, orderTokenId);

        clearOrderTokenId();
        successCallback(order);
      } catch (err: unknown) {
        sendMessageToParent({
          event: 'order.error',
          payload: {
            error:
              err instanceof CohortError
                ? err.code
                : err instanceof Error
                  ? err.message
                  : String(err),
          },
        });
        handleOrderSessionErrors(err);
      } finally {
        setIsProcessing(false);
      }
    },
    [
      campaign,
      createFreeOrder,
      redeemCode,
      orderTokenId,
      successCallback,
      handleOrderSessionErrors,
      clearOrderTokenId,
    ]
  );

  return (
    <form onSubmit={handleFreeClaimClick} className="space-y-5">
      <div className=" flex items-center">
        <Checkbox checked={tosChecked} setChecked={() => setTosChecked(!tosChecked)} testId="tos" />

        <CampaignTos campaign={campaign} checked={tosChecked} setChecked={setTosChecked} />
      </div>
      <PrimaryButton
        testId="claim"
        type="submit"
        isLoading={isProcessing}
        text={t('claimForFree')}
        disabled={!tosChecked}
        className="w-full"
        tracking={{namespace: 'store.checkout.claimForFree'}}
      />
    </form>
  );
};
